import { useCallback, useRef, useState } from "react";

// Chunking policy — ported from ai-medical-consultation-assistant
const MIN_CHUNK_MS = 40000; // don't cut before this unless forced
const MAX_CHUNK_MS = 60000; // hard cutoff regardless of silence
const SILENCE_HOLD_MS = 700; // how long the signal must stay quiet to count as a natural pause
const SILENCE_RMS_THRESHOLD = 0.012; // empirical threshold on normalized RMS (0..1)
const ANALYSE_INTERVAL_MS = 100;
const MAX_SESSION_MS = 7 * 60 * 1000; // hard cap on a single recording session — mic is cut automatically at 7 min

/**
 * Continuously records microphone audio and emits one Blob per "intelligent"
 * chunk: at least MIN_CHUNK_MS long, cut on the next natural silence after
 * that, and never allowed to exceed MAX_CHUNK_MS.
 *
 * onChunkReady(blob, chunkIndex) is called every time a chunk is finalized.
 */
export function useAudioRecorder({ onChunkReady, onError, onSessionEnd } = {}) {
  const [isRecording, setIsRecording] = useState(false);
  const [chunkCount, setChunkCount] = useState(0);
  const [lastChunkAt, setLastChunkAt] = useState(null);

  const streamRef = useRef(null);
  const audioCtxRef = useRef(null);
  const analyserRef = useRef(null);
  const dataArrayRef = useRef(null);
  const recorderRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const chunkStartRef = useRef(0);
  const sessionStartRef = useRef(0); // whole-session clock (0 = not recording); drives the 7-min cap + duration report
  const silenceStartRef = useRef(null);
  const analyseTimerRef = useRef(null);
  const stoppingRef = useRef(false);
  const recordedBytesRef = useRef([]);
  const mimeTypeRef = useRef("audio/webm");

  const computeRms = useCallback(() => {
    const analyser = analyserRef.current;
    const data = dataArrayRef.current;
    if (!analyser || !data) return 0;
    analyser.getByteTimeDomainData(data);
    let sumSquares = 0;
    for (let i = 0; i < data.length; i++) {
      const normalized = (data[i] - 128) / 128;
      sumSquares += normalized * normalized;
    }
    return Math.sqrt(sumSquares / data.length);
  }, []);

  const beginNewChunkRecorder = useCallback(() => {
    const stream = streamRef.current;
    if (!stream) return;

    const recorder = new MediaRecorder(stream, { mimeType: mimeTypeRef.current });
    recordedBytesRef.current = [];

    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size > 0) {
        recordedBytesRef.current.push(event.data);
      }
    };

    recorder.onstop = () => {
      const blob = new Blob(recordedBytesRef.current, { type: mimeTypeRef.current });
      recordedBytesRef.current = [];
      const index = chunkIndexRef.current;
      chunkIndexRef.current += 1;
      setChunkCount(chunkIndexRef.current);
      setLastChunkAt(Date.now());
      if (blob.size > 0) {
        onChunkReady?.(blob, index);
      }
      if (!stoppingRef.current) {
        chunkStartRef.current = Date.now();
        silenceStartRef.current = null;
        beginNewChunkRecorder();
      }
    };

    recorder.start();
    recorderRef.current = recorder;
  }, [onChunkReady]);

  const cutCurrentChunk = useCallback(() => {
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
  }, []);

  const stopRecording = useCallback(() => {
    stoppingRef.current = true;
    if (analyseTimerRef.current) {
      clearInterval(analyseTimerRef.current);
      analyseTimerRef.current = null;
    }
    const recorder = recorderRef.current;
    if (recorder && recorder.state !== "inactive") {
      recorder.stop();
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    audioCtxRef.current?.close().catch(() => {});
    audioCtxRef.current = null;
    setIsRecording(false);

    // Report the wall-clock length of the session exactly once. The unmount /
    // pagehide cleanup calls stopRecording again — that second call is a no-op here.
    if (sessionStartRef.current !== 0) {
      const durationSec = Math.round((Date.now() - sessionStartRef.current) / 1000);
      sessionStartRef.current = 0;
      onSessionEnd?.(durationSec);
    }
  }, [onSessionEnd]);

  const analyseLoop = useCallback(() => {
    // Hard 7-minute cap: cut the microphone completely (not just the chunk).
    if (
      sessionStartRef.current !== 0 &&
      Date.now() - sessionStartRef.current >= MAX_SESSION_MS
    ) {
      stopRecording();
      return;
    }

    const elapsed = Date.now() - chunkStartRef.current;
    const rms = computeRms();

    if (rms < SILENCE_RMS_THRESHOLD) {
      if (silenceStartRef.current === null) {
        silenceStartRef.current = Date.now();
      }
    } else {
      silenceStartRef.current = null;
    }

    const silenceDuration = silenceStartRef.current ? Date.now() - silenceStartRef.current : 0;
    const naturalPauseReached = elapsed >= MIN_CHUNK_MS && silenceDuration >= SILENCE_HOLD_MS;
    const hardLimitReached = elapsed >= MAX_CHUNK_MS;

    if (naturalPauseReached || hardLimitReached) {
      cutCurrentChunk();
    }
  }, [computeRms, cutCurrentChunk, stopRecording]);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const preferredMime = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"].find((type) =>
        MediaRecorder.isTypeSupported?.(type),
      );
      mimeTypeRef.current = preferredMime || "audio/webm";

      const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
      const audioCtx = new AudioContextCtor();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 1024;
      source.connect(analyser);

      audioCtxRef.current = audioCtx;
      analyserRef.current = analyser;
      dataArrayRef.current = new Uint8Array(analyser.fftSize);

      stoppingRef.current = false;
      chunkIndexRef.current = 0;
      setChunkCount(0);
      chunkStartRef.current = Date.now();
      silenceStartRef.current = null;

      beginNewChunkRecorder();
      analyseTimerRef.current = setInterval(analyseLoop, ANALYSE_INTERVAL_MS);
      sessionStartRef.current = Date.now(); // set last: only a fully-started session reports a duration
      setIsRecording(true);
    } catch (err) {
      onError?.(err);
    }
  }, [analyseLoop, beginNewChunkRecorder, onError]);

  return {
    isRecording,
    chunkCount,
    lastChunkAt,
    startRecording,
    stopRecording,
  };
}
